import React, { useState, useEffect, useRef } from 'react'
import {
  Card,
  Row,
  Col,
  Checkbox,
  Input,
  Button,
  List,
  Avatar,
  Typography,
  Space,
  Divider,
  Tag,
  Tooltip,
  message,
  Badge,
  Tabs,
  Popconfirm,
  Empty
} from 'antd'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faComments,
  faPaperPlane,
  faEnvelope,
  faSms,
  faPhone,
  faCheckCircle,
  faTimesCircle,
  faStickyNote,
  faTrash,
  faEdit,
  faSync
} from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import dayjs from 'dayjs'
import { addMessageListener } from '../../../lib/websocket-utils'
import {
  getInvoiceComms,
  createInvoiceComms,
  updateInvoiceComms,
  addChatMessage,
  updateCommStatus,
  addNote,
  deleteNote,
  deleteMessage
} from '../utils/comms-utils'
import './styles/invoice-comms.css'

const { Text, Title } = Typography
const { TextArea } = Input
const { TabPane } = Tabs

const InvoiceComms = ({ invoiceRef, invoiceData, disabled = false }) => {
  const authState = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [commsData, setCommsData] = useState(null)
  const [chatMessage, setChatMessage] = useState('')
  const [newNote, setNewNote] = useState('')
  const [editingNote, setEditingNote] = useState(null)
  const [activeTab, setActiveTab] = useState('communications')
  const chatEndRef = useRef(null)

  // Communication status tracking
  const [commStatus, setCommStatus] = useState({
    emailSent: false,
    smsSent: false,
    whatsappSent: false
  })

  // Load communications data
  useEffect(() => {
    if (invoiceRef) {
      loadCommsData()
    }
  }, [invoiceRef])

  // Auto-scroll chat to bottom
  useEffect(() => {
    scrollToBottom()
  }, [commsData?.chatMessages])

  // Set up WebSocket listener for real-time updates
  useEffect(() => {
    if (!invoiceRef) return

    const unsubscribe = addMessageListener('invoice_communications', (data) => {
      // Only update if this message is for our invoice
      if (data.data?.invoiceRef === invoiceRef) {
        console.log('Received real-time update for invoice communications:', data.action)

        switch (data.action) {
          case 'create':
          case 'update':
          case 'message_added':
          case 'status_updated':
          case 'note_added':
          case 'note_deleted':
          case 'message_deleted':
            // Refresh the communications data
            loadCommsData()
            break
          default:
            console.log('Unhandled invoice_communications action:', data.action)
        }
      }
    })

    return () => {
      if (unsubscribe) unsubscribe()
    }
  }, [invoiceRef])

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const loadCommsData = async () => {
    try {
      setLoading(true)
      const data = await getInvoiceComms(invoiceRef)

      if (data && data.length > 0) {
        const comms = data[0]
        setCommsData(comms)
        setCommStatus(
          comms.communicationStatus || {
            emailSent: false,
            smsSent: false,
            whatsappSent: false
          }
        )
      } else {
        // Create new communications record
        const newComms = await createInvoiceComms({
          invoiceRef,
          entityRef: invoiceData?.entityRef,
          patientRef: invoiceData?.userRef,
          communicationStatus: {
            emailSent: false,
            smsSent: false,
            whatsappSent: false
          },
          chatMessages: [],
          notes: [],
          createdAt: new Date().toISOString(),
          createdBy: authState.agiliteUser?._id
        })
        setCommsData(newComms)
      }
    } catch (error) {
      message.error('Failed to load communications data')
      console.error('Load comms error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCommStatusChange = async (type, checked) => {
    try {
      const updatedStatus = { ...commStatus, [type]: checked }
      setCommStatus(updatedStatus)

      if (commsData?._id) {
        await updateCommStatus(commsData._id, updatedStatus)

        // Add automatic tracking message
        const trackingMessage = {
          message: `${type.replace('Sent', '').toUpperCase()} ${checked ? 'sent' : 'marked as not sent'}`,
          sender: authState.agiliteUser?._id,
          senderName: `${authState.agiliteUser?.firstName} ${authState.agiliteUser?.lastName}`,
          timestamp: new Date().toISOString(),
          type: 'system'
        }

        const updatedComms = await addChatMessage(commsData._id, trackingMessage)
        setCommsData(updatedComms)

        message.success(`${type.replace('Sent', '').toUpperCase()} status updated`)
      }
    } catch (error) {
      message.error('Failed to update communication status')
      console.error('Update comm status error:', error)
    }
  }

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !commsData?._id) return

    try {
      const messageData = {
        message: chatMessage.trim(),
        sender: authState.agiliteUser?._id,
        senderName: `${authState.agiliteUser?.firstName} ${authState.agiliteUser?.lastName}`,
        timestamp: new Date().toISOString(),
        type: 'user'
      }

      const updatedComms = await addChatMessage(commsData._id, messageData)
      setCommsData(updatedComms)
      setChatMessage('')
    } catch (error) {
      message.error('Failed to send message')
      console.error('Send message error:', error)
    }
  }

  const handleAddNote = async () => {
    if (!newNote.trim() || !commsData?._id) return

    try {
      const noteData = {
        note: newNote.trim(),
        createdBy: authState.agiliteUser?._id,
        createdByName: `${authState.agiliteUser?.firstName} ${authState.agiliteUser?.lastName}`,
        createdAt: new Date().toISOString(),
        id: Date.now().toString()
      }

      const updatedComms = await addNote(commsData._id, noteData)
      setCommsData(updatedComms)
      setNewNote('')
      message.success('Note added successfully')
    } catch (error) {
      message.error('Failed to add note')
      console.error('Add note error:', error)
    }
  }

  const handleDeleteNote = async (noteId) => {
    try {
      const updatedComms = await deleteNote(commsData._id, noteId)
      setCommsData(updatedComms)
      message.success('Note deleted successfully')
    } catch (error) {
      message.error('Failed to delete note')
      console.error('Delete note error:', error)
    }
  }

  const handleDeleteMessage = async (messageIndex) => {
    try {
      const updatedComms = await deleteMessage(commsData._id, messageIndex)
      setCommsData(updatedComms)
      message.success('Message deleted successfully')
    } catch (error) {
      message.error('Failed to delete message')
      console.error('Delete message error:', error)
    }
  }

  const renderCommunicationTracking = () => (
    <div className='comm-tracking-section'>
      <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
        <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 8 }} />
        Communication Status
      </Title>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size='small' className='comm-status-card'>
            <Space align='center'>
              <FontAwesomeIcon
                icon={faEnvelope}
                style={{
                  color: commStatus.emailSent ? '#52c41a' : '#d9d9d9',
                  fontSize: 20
                }}
              />
              <div>
                <div style={{ fontWeight: 500 }}>Email</div>
                <Checkbox
                  checked={commStatus.emailSent}
                  onChange={(e) => handleCommStatusChange('emailSent', e.target.checked)}
                  disabled={disabled}
                >
                  Sent
                </Checkbox>
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card size='small' className='comm-status-card'>
            <Space align='center'>
              <FontAwesomeIcon
                icon={faSms}
                style={{
                  color: commStatus.smsSent ? '#52c41a' : '#d9d9d9',
                  fontSize: 20
                }}
              />
              <div>
                <div style={{ fontWeight: 500 }}>SMS</div>
                <Checkbox
                  checked={commStatus.smsSent}
                  onChange={(e) => handleCommStatusChange('smsSent', e.target.checked)}
                  disabled={disabled}
                >
                  Sent
                </Checkbox>
              </div>
            </Space>
          </Card>
        </Col>

        <Col xs={24} sm={8}>
          <Card size='small' className='comm-status-card'>
            <Space align='center'>
              <FontAwesomeIcon
                icon={faWhatsapp}
                style={{
                  color: commStatus.whatsappSent ? '#52c41a' : '#d9d9d9',
                  fontSize: 20
                }}
              />
              <div>
                <div style={{ fontWeight: 500 }}>WhatsApp</div>
                <Checkbox
                  checked={commStatus.whatsappSent}
                  onChange={(e) => handleCommStatusChange('whatsappSent', e.target.checked)}
                  disabled={disabled}
                >
                  Sent
                </Checkbox>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {(commStatus.emailSent || commStatus.smsSent || commStatus.whatsappSent) && (
        <div style={{ marginTop: 16 }}>
          <Text type='success'>
            <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 4 }} />
            Communication tracking active
          </Text>
        </div>
      )}
    </div>
  )

  const renderChat = () => (
    <div className='chat-section'>
      <div className='chat-header'>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
            <FontAwesomeIcon icon={faComments} style={{ marginRight: 8 }} />
            Admin Team Chat
          </Title>
          <Button
            type='text'
            size='small'
            icon={<FontAwesomeIcon icon={faSync} />}
            onClick={loadCommsData}
            loading={loading}
            style={{ marginLeft: 8 }}
            title='Refresh communications'
          />
        </div>
        <Badge count={commsData?.chatMessages?.length || 0} />
      </div>

      <div className='chat-messages' style={{ maxHeight: 300, overflowY: 'auto', padding: '8px 0' }}>
        {commsData?.chatMessages && commsData.chatMessages.length > 0 ? (
          <List
            dataSource={commsData.chatMessages}
            renderItem={(item, index) => (
              <List.Item
                key={index}
                className={`chat-message ${item.type === 'system' ? 'system-message' : 'user-message'}`}
                style={{
                  padding: '8px 0',
                  border: 'none'
                }}
              >
                <List.Item.Meta
                  avatar={
                    item.type === 'system' ? (
                      <Avatar icon={<FontAwesomeIcon icon={faCheckCircle} />} size='small' />
                    ) : (
                      <Avatar size='small'>
                        {item.senderName
                          ?.split(' ')
                          .map((n) => n[0])
                          .join('')}
                      </Avatar>
                    )
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>
                        {item.senderName}
                        {item.type === 'system' && (
                          <Tag size='small' color='blue' style={{ marginLeft: 4 }}>
                            System
                          </Tag>
                        )}
                      </span>
                      <div>
                        <Text type='secondary' style={{ fontSize: 11 }}>
                          {dayjs(item.timestamp).format('MMM D, HH:mm')}
                        </Text>
                        {!disabled && item.sender === authState.agiliteUser?._id && (
                          <Popconfirm
                            title='Delete this message?'
                            onConfirm={() => handleDeleteMessage(index)}
                            okText='Yes'
                            cancelText='No'
                          >
                            <Button
                              type='text'
                              size='small'
                              icon={<FontAwesomeIcon icon={faTrash} />}
                              style={{ marginLeft: 4, color: '#ff4d4f' }}
                            />
                          </Popconfirm>
                        )}
                      </div>
                    </div>
                  }
                  description={
                    <div style={{ marginTop: 4 }}>
                      <Text>{item.message}</Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description='No messages yet' image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '20px 0' }} />
        )}
        <div ref={chatEndRef} />
      </div>

      {!disabled && (
        <div className='chat-input' style={{ marginTop: 16 }}>
          <Row gutter={8}>
            <Col flex='auto'>
              <Input.TextArea
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                placeholder='Type your message to the admin team...'
                autoSize={{ minRows: 2, maxRows: 4 }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleSendMessage()
                  }
                }}
              />
            </Col>
            <Col>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faPaperPlane} />}
                onClick={handleSendMessage}
                disabled={!chatMessage.trim()}
                style={{ height: 'auto', minHeight: 40 }}
              >
                Send
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </div>
  )

  const renderNotes = () => (
    <div className='notes-section'>
      <div className='notes-header'>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0, color: '#1890ff' }}>
            <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: 8 }} />
            Admin Notes
          </Title>
          <Button
            type='text'
            size='small'
            icon={<FontAwesomeIcon icon={faSync} />}
            onClick={loadCommsData}
            loading={loading}
            style={{ marginLeft: 8 }}
            title='Refresh communications'
          />
        </div>
        <Badge count={commsData?.notes?.length || 0} />
      </div>

      {!disabled && (
        <div className='add-note' style={{ marginTop: 16, marginBottom: 16 }}>
          <Row gutter={8}>
            <Col flex='auto'>
              <TextArea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder='Add a private admin note...'
                autoSize={{ minRows: 2, maxRows: 4 }}
              />
            </Col>
            <Col>
              <Button
                type='primary'
                icon={<FontAwesomeIcon icon={faStickyNote} />}
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                style={{ height: 'auto', minHeight: 40 }}
              >
                Add Note
              </Button>
            </Col>
          </Row>
        </div>
      )}

      <div className='notes-list'>
        {commsData?.notes && commsData.notes.length > 0 ? (
          <List
            dataSource={commsData.notes}
            renderItem={(note) => (
              <List.Item
                key={note.id}
                actions={
                  !disabled && note.createdBy === authState.agiliteUser?._id
                    ? [
                        <Popconfirm
                          title='Delete this note?'
                          onConfirm={() => handleDeleteNote(note.id)}
                          okText='Yes'
                          cancelText='No'
                        >
                          <Button type='text' size='small' danger icon={<FontAwesomeIcon icon={faTrash} />}>
                            Delete
                          </Button>
                        </Popconfirm>
                      ]
                    : []
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar size='small'>
                      {note.createdByName
                        ?.split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </Avatar>
                  }
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>{note.createdByName}</span>
                      <Text type='secondary' style={{ fontSize: 12 }}>
                        {dayjs(note.createdAt).format('MMM D, YYYY HH:mm')}
                      </Text>
                    </div>
                  }
                  description={
                    <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>
                      <Text>{note.note}</Text>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
        ) : (
          <Empty description='No notes yet' image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '20px 0' }} />
        )}
      </div>
    </div>
  )

  if (!invoiceRef) {
    return (
      <Card>
        <Empty description='Save invoice first to enable communications' />
      </Card>
    )
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <FontAwesomeIcon icon={faComments} style={{ marginRight: 8, color: '#1890ff' }} />
            <span>Communications & Notes</span>
          </div>
          <Button
            type='text'
            size='small'
            icon={<FontAwesomeIcon icon={faSync} />}
            onClick={loadCommsData}
            loading={loading}
            title='Refresh all communications'
          >
            Refresh
          </Button>
        </div>
      }
      loading={loading}
      className='invoice-comms-card'
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'communications',
            label: (
              <span>
                <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: 4 }} />
                Tracking
              </span>
            ),
            children: renderCommunicationTracking()
          },
          {
            key: 'chat',
            label: (
              <span>
                <FontAwesomeIcon icon={faComments} style={{ marginRight: 4 }} />
                Team Chat
                {commsData?.chatMessages?.length > 0 && (
                  <Badge count={commsData.chatMessages.length} style={{ marginLeft: 4 }} />
                )}
              </span>
            ),
            children: renderChat()
          },
          {
            key: 'notes',
            label: (
              <span>
                <FontAwesomeIcon icon={faStickyNote} style={{ marginRight: 4 }} />
                Notes
                {commsData?.notes?.length > 0 && <Badge count={commsData.notes.length} style={{ marginLeft: 4 }} />}
              </span>
            ),
            children: renderNotes()
          }
        ]}
      />
    </Card>
  )
}

export default InvoiceComms
